<template>
  <section class="chapter-intro-teacher-section">
    <h1 class="chapter-intro-heading">
      {{ $t('components.chapter-intro.teacher-section-heading') }}
    </h1>
    <div
      v-if="teacherDescription"
      class="chapter-intro-section intro-text"
      v-html="teacherDescription"
    />

    <div v-if="homework.length > 0" class="chapter-intro-section">
      <h2 class="teacher-content-heading">
        <ContentTypeIcon :content-type="ContentType.HOMEWORK" />
        {{ $t('labels.homework') }}
      </h2>
      <SortableTable
        :data="homework"
        :columns="homeworkColumns"
        row-key="id"
        :lock-sort="true"
        :stackable="true"
      >
        <template v-slot="{ item }">
          <td>
            <PreviewLightPopup
              :meta-model-type="MetaModelType.HOMEWORK"
              :content-id="item.id"
              :name="item.name"
              :enable-add-button="true"
            >
              <FaIcon icon="search" class="teacher-content-preview-icon" />
              {{ item.name }}
            </PreviewLightPopup>
          </td>
          <td>
            <template v-if="hasHomeworkDetails(item.id)">
              {{ arrayToString(item.topics.map(({ name }) => name)) }}
            </template>
            <LoadingAnimation v-else />
          </td>
          <td>
            <template v-if="hasHomeworkDetails(item.id)">
              {{
                $t('question.question-count-short', {
                  count: item.questionCount,
                  formattedCount: formatNumber(item.questionCount),
                })
              }}
            </template>
            <LoadingAnimation v-else />
          </td>
          <td>
            {{ classSpanToName(item.minClass, item.maxClass) }}
          </td>
          <td class="right">
            <ThreeDButton
              class="add-content-button"
              @click="addHomework(item, $event)"
            >
              <FaIcon icon="plus" />
              {{ $t('actions.add') }}
            </ThreeDButton>
          </td>
        </template>
      </SortableTable>
    </div>

    <div v-if="pdfAssignments.length > 0" class="chapter-intro-section">
      <h2 class="teacher-content-heading">
        <ContentTypeIcon :content-type="ContentType.PDF_ASSIGNMENT" />
        {{ $t('labels.pdf-assignments') }}
      </h2>
      <SortableTable
        :data="pdfAssignments"
        :columns="pdfAssignmentColumns"
        row-key="id"
        :lock-sort="true"
        :stackable="true"
      >
        <template v-slot="{ item }">
          <td>
            <PreviewLightPopup
              :meta-model-type="MetaModelType.PDFASSIGNMENT"
              :content-id="item.id"
            >
              <FaIcon icon="search" class="teacher-content-preview-icon" />
              {{ item.name }}
            </PreviewLightPopup>
          </td>
          <td>
            {{ arrayToString(item.topics) }}
          </td>
          <td>
            {{ classSpanToName(item.minClass, item.maxClass) }}
          </td>
        </template>
      </SortableTable>
    </div>

    <div v-if="activities.length > 0" class="chapter-intro-section">
      <h2 class="teacher-content-heading">
        <ContentTypeIcon :content-type="ContentType.ACTIVITY" />
        {{ $t('labels.activities') }}
      </h2>
      <SortableTable
        :data="activities"
        :columns="activityColumns"
        row-key="id"
        :lock-sort="true"
        :stackable="true"
      >
        <template v-slot="{ item: { activity, error } }">
          <template v-if="activity">
            <td>
              <PreviewLightPopup
                :meta-model-type="MetaModelType.ACTIVITY"
                :content-id="activity.id"
                :enable-print-button="true"
              >
                <FaIcon icon="search" class="teacher-content-preview-icon" />
                {{ activity.name }}
              </PreviewLightPopup>
            </td>
            <td>
              {{ arrayToString(activity.topics) }}
              {{ activity.topicId ? getTopicName(activity.topicId) : '' }}
            </td>
            <td>
              {{
                activity.estimatedTime
                  ? formatDuration(activity.estimatedTime * MINUTE)
                  : ''
              }}
            </td>
            <td>
              {{ classSpanToName(activity.minClass, activity.maxClass) }}
            </td>
          </template>
          <td v-else colspan="4">
            <ErrorMessage v-if="error">
              <p>{{ error }}</p>
            </ErrorMessage>
            <LoadingAnimation v-else />
          </td>
        </template>
      </SortableTable>
    </div>
  </section>
</template>

<script>
import ContentTypeIcon from '@/components/ui/ContentTypeIcon'
import ErrorMessage from '@/components/ui/ErrorMessage'
import FaIcon from '@/components/ui/FaIcon'
import LoadingAnimation from '@/components/LoadingAnimation'
import PreviewLightPopup from '@/components/contentsearch/PreviewLightPopup'
import SortableTable from '@/components/ui/SortableTable'
import ThreeDButton from '@/components/ThreeDButton'

import { classSpanToName } from '@/utils/class-level'
import { formatNumber, formatSpan } from '@/utils/number'
import { arrayToString } from '@/utils/string'
import { MINUTE, formatDuration } from '@/utils/date'

import { ContentType, MetaModelType } from '@/enums'

import { ActivityStoreNamespacedTypes } from '@/store/activities'
import { HomeworkStoreNamespacedTypes } from '@/store/homework'
import { LevelsStoreNamespacedTypes } from '@/store/levels'
import { PdfAssignmentsStoreNamespacedTypes } from '@/store/pdf-assignments'
import { TopicsStoreNamespacedTypes } from '@/store/topics'

export default {
  name: 'ChapterIntroTeacherSection',
  components: {
    ContentTypeIcon,
    ErrorMessage,
    FaIcon,
    LoadingAnimation,
    PreviewLightPopup,
    SortableTable,
    ThreeDButton,
  },
  props: {
    chapterData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      ContentType,
      MetaModelType,
      MINUTE,
      activityColumns: [
        { key: 'name', name: this.$t('labels.name') },
        { key: 'topic', name: this.$t('labels.topic') },
        { key: 'time', name: this.$t('labels.time') },
        {
          key: 'class-level',
          name: this.$t('labels.class-level'),
        },
      ],
      homeworkColumns: [
        { key: 'name', name: this.$t('labels.name') },
        { key: 'topic', name: this.$t('labels.topic') },
        {
          key: 'question-count',
          name: this.$t('labels.question-count'),
        },
        {
          key: 'class-level',
          name: this.$t('labels.class-level'),
        },
      ],
      pdfAssignmentColumns: [
        { key: 'name', name: this.$t('labels.name') },
        { key: 'topic', name: this.$t('labels.topic') },
        {
          key: 'class-level',
          name: this.$t('labels.class-level'),
        },
      ],
    }
  },
  computed: {
    activityIds() {
      return this.chapterData.activities
        ? Object.values(this.chapterData.activities).map(
            ({ activityId }) => activityId
          )
        : []
    },
    activities() {
      return this.activityIds.map(id => ({
        activity: this.$store.getters[
          ActivityStoreNamespacedTypes.getters.GET_ACTIVITY
        ](id),
        error: this.$store.getters[
          ActivityStoreNamespacedTypes.getters.GET_ACTIVITY_ERROR
        ](id),
      }))
    },
    homework() {
      return this.chapterData.homeworkIds.map(homeworkId =>
        this.$store.getters[HomeworkStoreNamespacedTypes.getters.GET_HOMEWORK](
          homeworkId
        )
      )
    },
    levels() {
      return this.$store.getters[LevelsStoreNamespacedTypes.getters.GET_ALL]()
    },
    pdfAssignments() {
      return this.chapterData.pdfAssignmentIds.map(pdfAssignmentId =>
        this.$store.getters[
          PdfAssignmentsStoreNamespacedTypes.getters.GET_PDF_ASSIGNMENT
        ](pdfAssignmentId)
      )
    },
    teacherDescription() {
      return this.chapterData.teacherDescription
    },
  },
  methods: {
    arrayToString,
    formatDuration,
    formatNumber,
    formatSpan,
    addHomework(homework, event) {
      // TODO Add missing data once FESSOR-7535 is solved
      edulab.pagescommon.books.Index.addSelectedContentElement(
        {
          id: homework.id,
          name: homework.name,
          type: MetaModelType.HOMEWORK,
        },
        event.target
      )
    },
    classSpanToName(min, max) {
      return classSpanToName(this.levels, min, max)
    },
    hasHomeworkDetails(homeworkId) {
      return this.$store.getters[
        HomeworkStoreNamespacedTypes.getters.HAS_HOMEWORK_DETAILS
      ](homeworkId)
    },
    getTopicName(topicId) {
      const topic = this.$store.getters[
        TopicsStoreNamespacedTypes.getters.GET_TOPIC
      ](topicId)

      return topic ? topic.name : ''
    },
  },
  watch: {
    activityIds: {
      handler(newIds) {
        newIds.forEach(id =>
          this.$store.dispatch(
            ActivityStoreNamespacedTypes.actions.FETCH_ACTIVITY,
            id
          )
        )
      },
      immediate: true,
    },
  },
  mounted() {
    this.$store.dispatch(LevelsStoreNamespacedTypes.actions.FETCH_ALL)
    this.$store.dispatch(TopicsStoreNamespacedTypes.actions.FETCH_ALL)
  },
}
</script>

<style lang="scss" scoped>
.chapter-intro-teacher-section {
  padding-top: var(--content-holder-margin);

  &:before {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    right: 0;
    margin-top: calc(var(--content-holder-margin) * -1);
    height: 0.2em;
    background: repeating-linear-gradient(
      90deg,
      transparent 0px,
      transparent 0.5em,
      $grayLight 0.5em 1em
    );
  }
}

.teacher-content-heading {
  text-transform: uppercase;
  margin-top: 0.25em;
  color: $blue;

  .content-type-icon {
    $size: 1.75em;

    width: $size;
    height: $size;
    margin-top: -0.25em;
    vertical-align: middle;
  }
}

.add-content-button {
  --button-color: #{$orange};
  --button-shadow-color: #{mix($orange, $gray)};

  font-size: 0.85em;
}

.teacher-content-preview-icon {
  color: $graySemiLight;
}
</style>
